import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { fetchBoxArticle, fetchTopicNav } from "@/lib/home-box.functions";
import { makeSeoDescription } from "@/lib/seo-description";
import { makeShortTitle } from "@/lib/seo-title";
import { lazifyHtml } from "@/lib/lazy-html";
import { SafeImage } from "@/components/site/SafeImage";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ShareBar } from "@/components/site/ShareBar";
import { AuthorBio } from "@/components/site/AuthorBio";
import { DeferUntilVisible } from "@/components/site/DeferUntilVisible";
import { RelatedBoxArticles } from "@/components/site/RelatedBoxArticles";

export const Route = createFileRoute("/topics/$slug/$article")({
  loader: async ({ params }) => {
    const [data, nav] = await Promise.all([
      fetchBoxArticle({ data: { boxSlug: params.slug, articleSlug: params.article } }),
      fetchTopicNav(),
    ]);
    if (!data) throw notFound();
    return {
      ...data,
      article: { ...data.article, body_html: lazifyHtml(data.article.body_html) },
      otherBoxes: nav.boxes.filter((b) => b.slug !== params.slug),
    };
  },


  head: ({ loaderData }) => {
    const rawTitle = loaderData?.article?.title ?? "Article";
    const title = makeShortTitle(rawTitle);
    const description = makeSeoDescription(loaderData?.article?.excerpt ?? "");
    const image = loaderData?.article?.hero_image_url;
    const absolute = image?.startsWith("http") ? image : image ? `https://lifewestway.com${image}` : null;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(absolute
          ? [
              { property: "og:image", content: absolute },
              { name: "twitter:image", content: absolute },
            ]
          : []),
      ],
      links: [
        ...(loaderData?.box?.slug && loaderData?.article?.slug
          ? [
              {
                rel: "canonical",
                href: `https://lifewestway.com/topics/${(loaderData as any).box.slug}/${(loaderData as any).article.slug}`,
              },
            ]
          : []),
        ...(image ? [{ rel: "preload", as: "image", href: image, fetchPriority: "high" as const }] : []),
      ],
    };
  },
  errorComponent: () => (
    <p className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">
      This article could not be loaded. Please try again.
    </p>
  ),
  notFoundComponent: () => (
    <p className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">Article not found.</p>
  ),
  component: BoxArticlePage,
});

function BoxArticlePage() {
  const data = Route.useLoaderData();
  if (!data) return null;
  const { box, article } = data;
  const otherBoxes = (data.otherBoxes ?? []) as { title: string; slug: string }[];


  const ld = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.published_at,
    dateModified: article.published_at,
    author: { "@type": "Organization", name: "LifeWestWay Editorial Team" },
    publisher: { "@type": "Organization", name: "LifeWestWay" },
    ...(article.hero_image_url ? { image: [article.hero_image_url] } : {}),
  };

  return (
    <article className="pb-16">
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: box.title, href: `/topics/${box.slug}` },
          { label: article.title },
        ]}
      />
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-accent">
          <Link to="/topics/$slug" params={{ slug: box.slug }} title={box.title}>
            {box.title}
          </Link>
        </p>
        <h1 className="mt-2 font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl">
          {article.title}
        </h1>
        <p className="mt-3 text-muted-foreground">{article.excerpt}</p>

        {article.hero_image_url ? (
          <SafeImage
            eager
            src={article.hero_image_url}
            alt={article.hero_image_alt || article.title}
            width={1200}
            height={675}
            className="mt-6 w-full rounded-2xl object-cover"
          />
        ) : null}

        <ShareBar title={article.title} />

        <div
          className="article-body mt-8"
          dangerouslySetInnerHTML={{ __html: article.body_html }}
        />

        <AuthorBio slug={article.slug} />
        
        <DeferUntilVisible minHeight={420}>
          <RelatedBoxArticles boxSlug={box.slug} currentArticleSlug={article.slug} />
        </DeferUntilVisible>


        <nav aria-label="Other topics" className="mt-12 border-t border-border pt-8">
          <h2 className="font-display text-xl font-bold text-accent">Browse other topics</h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {otherBoxes.map((b) => (
              <li key={b.slug}>
                <Link
                  to="/topics/$slug"
                  params={{ slug: b.slug }}
                  title={b.title}
                  className="inline-block rounded-full border border-accent/30 px-3 py-1.5 text-xs font-semibold text-accent hover:border-accent"
                >
                  {b.title}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/topics"
                title="All LifeWestWay topics"
                className="inline-block rounded-full border border-accent/30 px-3 py-1.5 text-xs font-semibold text-accent hover:border-accent"
              >
                All topics
              </Link>
            </li>
            <li>
              <Link
                to="/latest"
                title="Latest LifeWestWay guides"
                className="inline-block rounded-full border border-accent/30 px-3 py-1.5 text-xs font-semibold text-accent hover:border-accent"
              >
                Latest guides
              </Link>
            </li>
          </ul>
        </nav>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
    </article>
  );
}
